import { Feature, Point } from 'geojson';
import { ComponentProps, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Source } from 'react-map-gl/mapbox';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import useReduxListItems from '@alltrails/lists/hooks/useReduxListItems';
import type TrailActivityType from '@alltrails/trails/types/TrailActivityType';
import { useAppSelector } from '../../redux';
import pinZoomPopularityMap from '../../utils/pinZoomPopularityMap';
import { serializedCoordinatesToCoordinates } from '../../utils/serializedCoordinates';
import { formatTrailLengthForPin } from '../../utils/svgDefinitions/formatTrailLengthForPin';
import ActiveTrailPolylines from './ActiveTrailPolylines';
import TrailPins from './TrailPins';
import TrailResultProperties from './TrailResultProperties';

const sourceId = 'trail-results-source';

export type TrailResultsProps = {
  activityType?: TrailActivityType; // Used to display the correct trail card stats
  hideLists?: boolean;
  handleFavoriteClick?: (trailId: number) => void;
} & Pick<
  ComponentProps<typeof TrailPins>,
  | 'cardLocation'
  | 'detailedCardLocation'
  | 'inspirationalPageSlug'
  | 'mapLocation'
  | 'onPinHover'
  | 'onTrailCardViewed'
  | 'onTrailNavigate'
  | 'getCTAProps'
  | 'getTrailCardUrl'
>;

const TrailResults = ({
  activityType,
  cardLocation,
  detailedCardLocation,
  inspirationalPageSlug,
  mapLocation,
  onPinHover,
  onTrailCardViewed,
  onTrailNavigate,
  hideLists,
  getCTAProps,
  getTrailCardUrl,
  handleFavoriteClick: handleFavoriteClickProp
}: TrailResultsProps) => {
  const isMobile = useIsMobileSizedScreen();
  const intl = useIntl();
  const { trailResults, mapZoomBucket, displayMetric } = useAppSelector(state => ({
    trailResults: state.map.trailResults,
    mapZoomBucket: state.map.mapZoomBucket,
    displayMetric: state.context.displayMetric
  }));
  const analyticsInfo = useMemo(
    () =>
      detailedCardLocation && mapLocation
        ? {
            detailedCardLocation,
            page: mapLocation
          }
        : undefined,
    [detailedCardLocation, mapLocation]
  );
  const {
    isFavorite: isSavedUtil,
    isComplete: isCompletedUtil,
    isVerified: isVerifiedUtil,
    handleFavoriteClick,
    listModal,
    signUpModal
  } = useReduxListItems(undefined, undefined, analyticsInfo);
  const isTrailSaved = useCallback((trailId: number) => isSavedUtil('trail', trailId), [isSavedUtil]);
  const isTrailCompleted = useCallback((trailId: number) => isCompletedUtil('trail', trailId), [isCompletedUtil]);
  const isTrailVerified = useCallback((trailId: number) => isVerifiedUtil('trail', trailId), [isVerifiedUtil]);
  const handleTrailFavoriteClick = useCallback(
    (trailId: number) => (handleFavoriteClickProp ? handleFavoriteClickProp(trailId) : handleFavoriteClick('trail', trailId)),
    [handleFavoriteClickProp, handleFavoriteClick]
  );

  const features = useMemo(() => {
    if (!trailResults) {
      return [];
    }

    const { maxDesktopRegularPins, maxMobileRegularPins, maxTotalDesktopPins, maxTotalMobilePins } = pinZoomPopularityMap(mapZoomBucket);
    const maxRegularPins = isMobile ? maxMobileRegularPins : (maxDesktopRegularPins ?? 0);
    const maxTotalPins = isMobile ? maxTotalMobilePins : maxTotalDesktopPins;
    let regularTrailsCount = 0; // Number of trails that are not collapsed
    let collapsedTrailsCount = 0;
    const trailResultsEntries = Object.entries(trailResults);

    let allFeatures = trailResultsEntries.reduce<Feature<Point, TrailResultProperties>[]>((allFeatures, [serializedCoordinates, trails]) => {
      const { lat, lng } = serializedCoordinatesToCoordinates(serializedCoordinates);
      const isClosed = trails.every(trail => trail.is_closed);
      let isSaved = false;
      let isCompleted = false;
      let isCollapsed = false;

      if (!hideLists) {
        isSaved = trails.every(trail => isTrailSaved(Number(trail.ID)));
        isCompleted = trails.every(trail => isTrailCompleted(Number(trail.ID)));
      }

      if (maxRegularPins !== undefined && regularTrailsCount < maxRegularPins) {
        regularTrailsCount++;
      } else if (maxRegularPins !== undefined && maxTotalPins !== undefined && collapsedTrailsCount + regularTrailsCount < maxTotalPins) {
        collapsedTrailsCount++;
        isCollapsed = true;
      }

      // For single trails, show the trail length; for trailheads, show the count
      let lengthDisplay: string | undefined;
      const popularity = trails?.[0]?.popularity;
      const priority = trails?.[0]?.priority; // When present trails are sorted by priority so the first trail is the highest priority
      const isSelected = trails?.some(trail => trail.isSelected);
      if (trails.length === 1 && trails[0].length) {
        lengthDisplay = formatTrailLengthForPin(intl, trails[0].length, displayMetric);
      } else {
        if (!hideLists && !isSaved && trails.some(trail => isTrailSaved(Number(trail.ID)))) {
          isSaved = true;
        }
        lengthDisplay = intl.formatMessage({ id: 'trailhead.pin.count', defaultMessage: '{count} trails' }, { count: trails.length });
      }

      return [
        ...allFeatures,
        {
          geometry: { type: 'Point', coordinates: [lng, lat] },
          properties: {
            isClosed,
            isSaved,
            serializedCoordinates,
            trailCount: trails.length,
            isCompleted,
            isCollapsed,
            lengthDisplay,
            popularity,
            priority,
            isSelected
          },
          type: 'Feature'
        }
      ];
    }, []);

    if (!!maxTotalPins && allFeatures.length > maxTotalPins) {
      allFeatures = allFeatures.slice(0, maxTotalPins);
    }
    allFeatures.reverse();
    return allFeatures;
  }, [trailResults, mapZoomBucket, isMobile, hideLists, isTrailSaved, isTrailCompleted, intl, displayMetric]);

  const { savedTrailCounts, completedTrailCounts } = useMemo(() => {
    const counts: Set<number> = new Set([]);
    const closedCounts: Set<number> = new Set([]);
    const savedCounts: Set<number> = new Set([]);
    const completedCounts: Set<number> = new Set([]);
    features.forEach(feature => {
      if (feature.properties.isClosed) {
        closedCounts.add(feature.properties.trailCount);
      } else if (feature.properties.isSaved) {
        savedCounts.add(feature.properties.trailCount);
      } else if (feature.properties.isCompleted) {
        completedCounts.add(feature.properties.trailCount);
      } else {
        counts.add(feature.properties.trailCount);
      }
    });
    return { savedTrailCounts: savedCounts, completedTrailCounts: completedCounts };
  }, [features]);

  const listProps = useMemo(
    () =>
      hideLists
        ? {}
        : {
            savedTrailCounts,
            completedTrailCounts,
            isFavorite: isTrailSaved,
            isComplete: isTrailCompleted,
            isVerified: isTrailVerified,
            handleFavoriteClick: handleTrailFavoriteClick,
            listModal,
            signUpModal
          },
    [
      hideLists,
      savedTrailCounts,
      completedTrailCounts,
      isTrailSaved,
      isTrailCompleted,
      isTrailVerified,
      handleTrailFavoriteClick,
      listModal,
      signUpModal
    ]
  );

  if (!features.length) {
    return null;
  }

  return (
    <>
      <Source id={sourceId} type="geojson" data={{ type: 'FeatureCollection', features }}>
        <TrailPins
          activityType={activityType}
          cardLocation={cardLocation}
          detailedCardLocation={detailedCardLocation}
          inspirationalPageSlug={inspirationalPageSlug}
          onPinHover={onPinHover}
          onTrailCardViewed={onTrailCardViewed}
          onTrailNavigate={onTrailNavigate}
          getTrailCardUrl={getTrailCardUrl}
          getCTAProps={getCTAProps}
          mapLocation={mapLocation}
          sourceId={sourceId}
          {...listProps}
        />
      </Source>
      <ActiveTrailPolylines />
    </>
  );
};

export default TrailResults;
